/**
 * 메뉴 생성 모달.
 * menuKey는 서버가 자동 생성하므로 입력받지 않는다.
 * 닫기는 「취소」만 허용(오버레이·ESC·X 비활성).
 * 연결 경로는 아직 메뉴에 매칭되지 않은 ROUTE_PATHS만 고른다.
 */
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import { cn } from '@/shared/lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import {
  collectUsedMenuUrls,
  fromMenuUrlSelectValue,
  listAvailableMenuUrlOptions,
  listParentOptions,
  ROOT_PARENT_VALUE,
  toMenuUrlSelectValue,
  type MenuAudience,
  type MenuTreeItem,
} from './settingsMenuData';

export type CreateMenuFormInput = {
  name: string;
  parentId: string | null;
  url: string;
  visible: boolean;
  audience: MenuAudience;
};

type CreateMenuModalProps = {
  open: boolean;
  items: MenuTreeItem[];
  /** 모달 열릴 때 기본 상위 (선택 중인 메뉴 기준) */
  defaultParentId: string | null;
  submitting?: boolean;
  onClose: () => void;
  /** 성공 시 null, 실패 시 에러 메시지 */
  onCreate: (input: CreateMenuFormInput) => Promise<string | null>;
};

export const CreateMenuModal: React.FC<CreateMenuModalProps> = ({
  open,
  items,
  defaultParentId,
  submitting = false,
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const [audience, setAudience] = useState<MenuAudience>('general');
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const parentOptions = useMemo(
    () => listParentOptions(items, null),
    [items],
  );

  const usedUrls = useMemo(() => collectUsedMenuUrls(items), [items]);

  const urlOptions = useMemo(
    () => listAvailableMenuUrlOptions(usedUrls),
    [usedUrls],
  );

  useEffect(() => {
    if (!open) return;
    setName('');
    setUrl('');
    setParentId(defaultParentId);
    setVisible(true);
    setAudience('general');
    setNameError(null);
    setFormError(null);
  }, [open, defaultParentId]);

  // 선택지가 바뀌어 현재 url이 목록에 없으면 없음으로 되돌린다
  useEffect(() => {
    if (!open || !url) return;
    if (!urlOptions.some((opt) => opt.value === url)) {
      setUrl('');
    }
  }, [open, url, urlOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setFormError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('메뉴명을 입력해주세요');
      return;
    }
    if (trimmedName.length > 100) {
      setNameError('메뉴명은 최대 100자입니다');
      return;
    }

    const error = await onCreate({
      name: trimmedName,
      parentId,
      url: url.trim(),
      visible,
      audience,
    });
    if (error) {
      if (error.includes('메뉴명')) {
        setNameError(error);
      } else {
        setFormError(error);
      }
    }
  };

  return (
    <Dialog
      open={open}
      // 오버레이·ESC로는 닫지 않음 — 「취소」만 onClose
      onOpenChange={() => undefined}
    >
      <DialogContent
        className="max-w-[480px]"
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-start text-left sm:text-left">
          <DialogTitle>메뉴 추가</DialogTitle>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <Field
            label="메뉴명"
            htmlFor="create-menu-name"
            required
            error={nameError}
          >
            <Input
              id="create-menu-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="메뉴명 입력"
              maxLength={100}
              autoFocus
            />
          </Field>

          <Field
            label="연결 경로"
            htmlFor="create-menu-url"
            hint="앱에 정의된 라우트만 표시됩니다. 그룹/대분류는 「없음」"
          >
            <Select
              value={toMenuUrlSelectValue(url)}
              onValueChange={(value) => setUrl(fromMenuUrlSelectValue(value))}
            >
              <SelectTrigger id="create-menu-url" className="w-full">
                <SelectValue placeholder="경로 선택" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {urlOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="상위 메뉴" htmlFor="create-menu-parent">
            <Select
              value={parentId ?? ROOT_PARENT_VALUE}
              onValueChange={(value) =>
                setParentId(value === ROOT_PARENT_VALUE ? null : value)
              }
            >
              <SelectTrigger id="create-menu-parent" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {parentOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="노출·권한 범위">
            <div className="flex flex-wrap gap-3">
              <div className="inline-flex rounded-lg bg-muted p-[3px]">
                {(
                  [
                    { value: true, label: '노출' },
                    { value: false, label: '숨김' },
                  ] as const
                ).map((opt) => {
                  const active = visible === opt.value;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setVisible(opt.value)}
                      className={cn(
                        'rounded-md px-[18px] py-[7px] text-xs transition-colors',
                        active
                          ? 'border border-border bg-background font-medium text-ruda-success shadow-sm'
                          : 'font-normal text-muted-foreground',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="inline-flex rounded-lg bg-muted p-[3px]">
                {(
                  [
                    { value: 'admin' as const, label: '관리자 전용' },
                    { value: 'general' as const, label: '일반' },
                  ] as const
                ).map((opt) => {
                  const active = audience === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAudience(opt.value)}
                      className={cn(
                        'rounded-md px-[18px] py-[7px] text-xs transition-colors',
                        active
                          ? 'border border-border bg-background font-medium text-ruda-success shadow-sm'
                          : 'font-normal text-muted-foreground',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Field>

          {formError ? (
            <p className="text-xs text-destructive">{formError}</p>
          ) : null}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className={popupSecondaryButtonClassName}
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button
              type="submit"
              className={popupPrimaryButtonClassName}
              disabled={submitting}
            >
              {submitting ? '추가 중…' : '추가'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
